/**
 * @fileoverview Main Entry Point
 * Boots the CAP.IO application with routing, state management, and page rendering.
 * @module main
 *
 * ROUTES:
 * - #/ (root)        → Stationgate splash page (cinematic intro)
 * - #/home           → Home dashboard with observatories, stress board, dispatches
 * - #/observatories  → Observatory index
 * - #/observatories/:id → Individual observatory overview
 * - #/observatories/:id/:subpage → Observatory sub-page (usd-core, fx-stress, etc.)
 */

// Import styles - order matters: tokens first, then base, then components, then theme
import "./styles/type.css";
import "./styles/space.css";
import "./styles/base.css";
import "./shared/styles/card.css";
import "./styles/terminal-gothic.css";
import "./styles/observatory-enhancements.css";
import "./styles/signals.css";
import "./styles/chart-mode.css";
import "./styles/csi-widget.css";
import "./styles/dispatches.css";
import "./styles/about.css";
import "./styles/signals-in-action.css";

// Core modules
import { parseRouteFromLocation, listenToRouteChanges } from "./core/router.js";
import { createAppState } from "./core/state.js";

// Page components
import { renderHomePage, initHomePage } from "./pages/home.js";
import { renderStationgatePage, initStationgateEffects, cleanupStationgate } from "./pages/stationgate.js";
import { renderObservatoriesPage } from "./pages/observatories.js";
import { renderObservatoryPage, cleanupObservatory } from "./pages/observatory.js";
import { renderForumPage } from "./pages/forum.js";
import { renderDispatchesPage, cleanupDispatchesPage } from "./pages/dispatches.js";
import { renderSignalsPage } from "./pages/signals.js";
import { renderChartsPage, cleanupChartsPage } from "./pages/charts.js";
import { renderManifestoPage, initManifestoEffects } from "./pages/manifesto.js";
import { renderAboutPage, initAboutEffects } from "./pages/about.js";
import { renderSignalsInActionPage, initSignalsInActionEffects, cleanupSignalsInActionPage } from "./pages/signals-in-action.js";

// Shared components
import { renderAppShell, setupMobileMenu } from "./shared/components/AppShell.js";
import { el } from "./shared/components/simpleRender.js";

// Observatory manifest for default time range
import { dollarLiquidityManifest } from "./observatories/index.js";

/**
 * Tracks the currently rendered page for cleanup.
 * @type {string|null}
 */
let currentPage = null;

/**
 * Tracks the current sub-page for observatory
 * @type {string|null}
 */
let currentSubPage = null;

/**
 * Renders a placeholder page for routes not yet implemented.
 */
function renderPlaceholderPage(pageName) {
  const displayName = pageName
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return el("div", { className: "cap-placeholder-page" }, [
    el("header", { className: "cap-placeholder-header" }, [
      el("h1", { className: "cap-page-title" }, [displayName]),
      el("p", { className: "cap-page-subtitle" }, ["This section is coming soon."]),
    ]),
    el("div", { className: "cap-panel" }, [
      el("p", {}, ["We're building out the ", displayName, " section. Check back soon for updates."]),
      el("p", { style: "margin-top: var(--cap-space-4);" }, [
        el("a", { href: "#/home" }, ["← Return Home"]),
      ]),
    ]),
  ]);
}

/**
 * Renders a 404 Not Found page.
 */
function renderNotFoundPage(page) {
  return el("div", { className: "cap-not-found-page" }, [
    el("h1", { className: "cap-page-title" }, ["Page Not Found"]),
    el("p", { className: "cap-page-subtitle" }, [`The page "${page}" does not exist.`]),
    el("p", {}, [el("a", { href: "#/home" }, ["← Return Home"])]),
  ]);
}

/**
 * Cleans up effects from the previous page.
 */
function cleanupPreviousPage(previousPage) {
  if (previousPage === "stationgate") {
    cleanupStationgate();
  } else if (previousPage === "observatory") {
    cleanupObservatory();
  } else if (previousPage === "charts") {
    cleanupChartsPage();
  } else if (previousPage === "dispatches") {
    cleanupDispatchesPage();
  } else if (previousPage === "signals-in-action") {
    cleanupSignalsInActionPage();
  }
}

/**
 * Main render function that routes to the appropriate view.
 */
async function render(route, timeRange, appRoot) {
  cleanupPreviousPage(currentPage);
  appRoot.innerHTML = "";

  let content;
  let hideShell = false;

  // Determine which page to render
  const pageName = route.page;

  switch (pageName) {
    case "stationgate":
      // Cinematic splash - fullscreen, no header/footer
      content = renderStationgatePage();
      hideShell = true;
      currentPage = "stationgate";
      currentSubPage = null;
      break;

    case "home":
      // Main dashboard
      content = renderHomePage();
      currentPage = "home";
      currentSubPage = null;
      break;

    case "observatories":
      content = renderObservatoriesPage();
      currentPage = "observatories";
      currentSubPage = null;
      break;

    case "observatory":
      // Pass subPage to the observatory renderer
      const subPage = route.subPage || "overview";
      content = await renderObservatoryPage(route.id || "", subPage, timeRange);
      currentPage = "observatory";
      currentSubPage = subPage;
      break;

    case "signals":
      content = renderSignalsPage();
      currentPage = "signals";
      currentSubPage = null;
      break;

    case "charts":
      try {
        console.log('[Charts] Attempting to render charts page');
        content = renderChartsPage();
        hideShell = true; // Chart Mode is full-screen
        currentPage = "charts";
        currentSubPage = null;
        console.log('[Charts] Successfully rendered charts page');
      } catch (err) {
        console.error('[Charts] Failed to render charts page:', err);
        content = renderNotFoundPage('charts (error: ' + err.message + ')');
        currentPage = "not_found";
        currentSubPage = null;
      }
      break;

    case "store":
      content = renderPlaceholderPage(pageName);
      currentPage = pageName;
      currentSubPage = null;
      break;

    case "dispatches":
      content = renderDispatchesPage();
      currentPage = "dispatches";
      currentSubPage = null;
      break;

    case "forum":
      content = renderForumPage();
      currentPage = "forum";
      currentSubPage = null;
      break;

    case "manifesto":
      content = renderManifestoPage();
      currentPage = "manifesto";
      currentSubPage = null;
      break;

    case "about":
      content = renderAboutPage();
      currentPage = "about";
      currentSubPage = null;
      break;

    case "signals-in-action":
      content = renderSignalsInActionPage();
      currentPage = "signals-in-action";
      currentSubPage = null;
      break;

    default:
      content = renderNotFoundPage(pageName);
      currentPage = "not_found";
      currentSubPage = null;
  }

  // Wrap in app shell
  const shell = renderAppShell({ route, content, hideShell });
  appRoot.appendChild(shell);

  // Post-render setup
  if (!hideShell) {
    setupMobileMenu();
  }

  // Initialize page-specific effects
  if (currentPage === "home") {
    requestAnimationFrame(() => initHomePage());
  } else if (currentPage === "stationgate") {
    requestAnimationFrame(() => initStationgateEffects());
  } else if (currentPage === "manifesto") {
    requestAnimationFrame(() => initManifestoEffects());
  } else if (currentPage === "about") {
    requestAnimationFrame(() => initAboutEffects());
  } else if (currentPage === "signals-in-action") {
    requestAnimationFrame(() => initSignalsInActionEffects());
  }

  window.scrollTo(0, 0);
}

/**
 * Initializes the application.
 */
async function init() {
  const appRoot = document.getElementById("app");
  if (!appRoot) {
    console.error("[CAP.IO] No #app element found");
    return;
  }

  const initialRoute = parseRouteFromLocation();
  const appState = createAppState(initialRoute, dollarLiquidityManifest.defaultTimeRange);

  console.log("[CAP.IO] Initializing with route:", initialRoute);

  await render(appState.getRoute(), appState.getTimeRange(), appRoot);

  listenToRouteChanges(async (newRoute) => {
    const currentRoute = appState.getRoute();
    // Also check subPage for observatory navigation
    const routeChanged = 
      currentRoute.page !== newRoute.page || 
      currentRoute.id !== newRoute.id ||
      currentRoute.subPage !== newRoute.subPage;
    
    if (routeChanged) {
      console.log("[CAP.IO] Route changed:", newRoute);
      appState.setRoute(newRoute);
      await render(newRoute, appState.getTimeRange(), appRoot);
    }
  });

  // Console branding
  console.log("%c🚂 CAP.IO", "font-size: 20px; font-weight: bold; color: #d3ac63;");
  console.log("%cWelcome to Capitulators.io", "font-size: 12px; color: #9ca3af;");
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
